import { defineElement, FC, useBindMethod, useElement, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { modalStore } from '@store/modals';

export interface ModalCloseConfirmationPopupElement {
    onConfirm(): void;
    onDismiss(): void;
    title?: Text;
    description?: Text;
    dismissButtonText?: Text;
    confirmButtonText?: Text;
    titleElement: HTMLParagraphElement;
    descriptionElement: HTMLParagraphElement;
    confirmCta: HTMLButtonElement;
    dismissCta: HTMLButtonElement;
    setData: (data: ModalCloseConfirmationPopupTranslations) => void;
}

type ModalCloseConfirmationPopupTranslations = {
    [K in keyof Pick<
        ModalCloseConfirmationPopupElement,
        'title' | 'description' | 'confirmButtonText' | 'dismissButtonText'
    >]: string;
};

const HIDDEN_CLASS = 'u-hidden';

const ModalCloseConfirmationPopup: FC<ModalCloseConfirmationPopupElement> = ({ element }) => {
    const [modalId] = useProp<string>('modalId');
    const [onConfirm] = useProp<Function>('onConfirm');
    const [onDismiss] = useProp<Function>('onDismiss');
    const [title] = useProp<Text | null>('title', null);
    const [description] = useProp<Text | null>('description', null);
    const [confirmButtonText] = useProp<Text | null>('confirmButtonText', null);
    const [dismissButtonText] = useProp<Text | null>('dismissButtonText', null);

    const modalCloseConfirmationPopup = useElement<HTMLDivElement>(
        '[js-hook-modal-close-confirmation-popup]',
    );

    useElement('[js-hook-modal-close-confirmation-popup-title]', { as: 'titleElement' });
    useElement('[js-hook-modal-close-confirmation-popup-description]', {
        as: 'descriptionElement',
    });
    useElement('[js-hook-modal-close-confirmation-popup-confirm-cta]', { as: 'confirmCta' });
    useElement('[js-hook-modal-close-confirmation-popup-dismiss-cta]', { as: 'dismissCta' });

    useBindMethod('setData', (data: ModalCloseConfirmationPopupTranslations) => {
        data.title && (element.titleElement.innerHTML = data.title);
        data.description && (element.descriptionElement.innerHTML = data.description);
        data.confirmButtonText && (element.confirmCta.innerHTML = data.confirmButtonText);
        data.dismissButtonText && (element.dismissCta.innerHTML = data.dismissButtonText);
    });

    modalStore.subscribe(
        ({ modals: { [modalId]: modal } }) => {
            if (!modal) return;

            if (modal.closeRequest) {
                modalCloseConfirmationPopup.current?.classList.remove(HIDDEN_CLASS);
            } else {
                modalCloseConfirmationPopup.current?.classList.add(HIDDEN_CLASS);
            }

            if (!modal.active) {
                modalCloseConfirmationPopup.current?.classList.add(HIDDEN_CLASS);
            }
        },
        ['modals'],
    );

    return (
        <div
            class="c-modal-close-confirmation-popup u-hidden"
            js-hook-modal-close-confirmation-popup>
            <p class="u-body-large-bold" js-hook-modal-close-confirmation-popup-title>
                {title}
            </p>
            <p js-hook-modal-close-confirmation-popup-description>{description}</p>
            <div class="modal-close-confirmation-popup__link-container">
                <button
                    class="u-content-link"
                    onClick={onDismiss}
                    js-hook-modal-close-confirmation-popup-dismiss-cta>
                    {dismissButtonText}
                </button>
                <button
                    class="u-content-link"
                    onClick={onConfirm}
                    js-hook-modal-close-confirmation-popup-confirm-cta>
                    {confirmButtonText}
                </button>
            </div>
        </div>
    );
};

ModalCloseConfirmationPopup.props = {
    modalId: {
        type: String,
        required: true,
    },
    onConfirm: {
        type: Function,
        required: true,
    },
    onDismiss: {
        type: Function,
        required: true,
    },
    title: {
        type: Text,
    },
    description: {
        type: Text,
        reflectToAttr: true,
    },
    confirmButtonText: {
        type: Text,
    },
    dismissButtonText: {
        type: Text,
    },
};

defineElement('modal-close-confirmation-popup', ModalCloseConfirmationPopup);
